import { Button } from '../../../Utility/Button';
import Input from '../../../Utility/FormElements/Input';
import Checkbox from '../../../Utility/FormElements/Checkbox';
import { StoreI18n, StoreSettings, DlpRegex } from '../../../../Types/StoreTypes';
import EditableList from '../../../Utility/EditableList';

interface DataProtectionProps {
    SettingsActions: any;
    i18n: StoreI18n['i18n'];
    Currentsettings: StoreSettings;
}

function DataProtection({ SettingsActions, i18n, Currentsettings }: DataProtectionProps) {
    return (
        <div className="w-full flex gap-5 flex-col">
            <h4>{i18n.translate({ id: 'dataprotection', sourceName: 'Settings' })}</h4>

            <div className="mt-3 mb-2">
                <div className="mb-2">
                    <Checkbox
                        checked={Currentsettings.userStatistic}
                        label={i18n.translate({
                            id: 'userStatistics',
                            sourceName: 'Settings',
                        })}
                        onChange={() =>
                            SettingsActions.updateCurrentSettings({
                                userStatistic: !Currentsettings.userStatistic,
                            })
                        }
                    />
                </div>
                <div className="mb-2">
                    <Checkbox
                        checked={Currentsettings.openaiDocumentProcessingEnabled}
                        onChange={(e) =>
                            SettingsActions.updateCurrentSettings({ openaiDocumentProcessingEnabled: e.target.checked })
                        }
                        label={i18n.translate({
                            id: 'openaiDocumentProcessingEnabled',
                            sourceName: 'Settings',
                        })}
                    />
                </div>
            </div>

            {Currentsettings.icapActive && (
                <label htmlFor="icapAddress" className="flex flex-col gap-2">
                    {i18n.translate({
                        id: 'icapAddress',
                        sourceName: 'Settings',
                    })}
                    <Input
                        inputType="string"
                        value={Currentsettings.icapAddress}
                        onChange={(icapAddress) => SettingsActions.updateCurrentSettings({ icapAddress: icapAddress })}
                        id={'icapAddress'}
                    />
                </label>
            )}

            <h5 className="mt-5">
                {i18n.translate({
                    id: 'sensitivityLabels',
                    sourceName: 'Settings',
                })}
            </h5>
            <div>
                {Currentsettings.sensitivityLabels.map((sensitivityLabel: any, index: number) => (
                    <EditableList
                        key={index}
                        itemValue={sensitivityLabel}
                        placeholder=""
                        handleChange={SettingsActions.handleSensitivityLabelChange}
                        handleRemove={SettingsActions.removeSensitivityLabel}
                        index={index}
                    />
                ))}
                <div className="w-fit">
                    <Button
                        variant="secondary"
                        onClick={() =>
                            SettingsActions.updateCurrentSettings({
                                sensitivityLabels: [...Currentsettings.sensitivityLabels, ''],
                            })
                        }
                    >
                        {i18n.translate({
                            id: 'addSensitivityLabel',
                            sourceName: 'Settings',
                        })}
                    </Button>
                </div>
            </div>

            <h5 className="mt-5">
                {i18n.translate({
                    id: 'dlpEntities',
                    sourceName: 'Settings',
                })}
            </h5>
            <div>
                {Object.entries(Currentsettings.availableDlpEntities || {}).map(([entityName, translatedLabel]) => {
                    const isEnabled = Currentsettings.dlpEntities.includes(entityName);

                    return (
                        <div key={entityName} className="flex items-center gap-3 mb-2">
                            <Checkbox
                                checked={isEnabled}
                                onChange={() => {
                                    const newEntities = isEnabled
                                        ? Currentsettings.dlpEntities.filter((entity) => entity !== entityName)
                                        : [...Currentsettings.dlpEntities, entityName];

                                    SettingsActions.updateCurrentSettings({
                                        dlpEntities: newEntities,
                                    });
                                }}
                            />
                            <div className="flex-1">{translatedLabel}</div>
                        </div>
                    );
                })}
            </div>

            <h5 className="mt-5">
                {i18n.translate({
                    id: 'dlpRegexes',
                    sourceName: 'Settings',
                })}
            </h5>
            <div>
                {Currentsettings.dlpRegexes.map((dlpRegex: DlpRegex, index: number) => (
                    <EditableList
                        key={index}
                        itemValue={dlpRegex}
                        placeholder={i18n.translate({ id: 'regex.placeholder', sourceName: 'Settings' })}
                        labelPlaceholder={i18n.translate({ id: 'label.placeholder', sourceName: 'Settings' })}
                        handleChange={SettingsActions.handleDlpRegexChange}
                        handleRemove={SettingsActions.removeDlpRegex}
                        index={index}
                        useLabel={true}
                    />
                ))}
                <div className="w-fit">
                    <Button
                        variant="secondary"
                        onClick={() =>
                            SettingsActions.updateCurrentSettings({
                                dlpRegexes: [...Currentsettings.dlpRegexes, { label: '', regex: '' }],
                            })
                        }
                    >
                        {i18n.translate({
                            id: 'addDlpRegex',
                            sourceName: 'Settings',
                        })}
                    </Button>
                </div>
            </div>

            <h5 className="mt-5">
                {i18n.translate({
                    id: 'allowList',
                    sourceName: 'Settings',
                })}
            </h5>
            <div>
                {Currentsettings.allowList.map((allowListItem: string, index: number) => (
                    <EditableList
                        key={index}
                        itemValue={allowListItem}
                        placeholder={i18n.translate({ id: 'allowList.placeholder', sourceName: 'Settings' })}
                        handleChange={SettingsActions.handleAllowListChange}
                        handleRemove={SettingsActions.removeAllowListItem}
                        index={index}
                    />
                ))}
                <div className="w-fit">
                    <Button
                        variant="secondary"
                        onClick={() =>
                            SettingsActions.updateCurrentSettings({
                                allowList: [...Currentsettings.allowList, ''],
                            })
                        }
                    >
                        {i18n.translate({ id: 'allowList.add', sourceName: 'Settings' })}
                    </Button>
                </div>
            </div>

            <h5 className="mt-5">{i18n.translate({ id: 'allowedProtocols', sourceName: 'Settings' })}</h5>
            <p>{i18n.translate({ id: 'allowedProtocols.description', sourceName: 'Settings' })}</p>
            <div>
                {(Currentsettings.additionalAllowedProtocols || []).map((proto: string, index: number) => (
                    <EditableList
                        key={index}
                        itemValue={proto}
                        placeholder={i18n.translate({
                            id: 'allowedProtocols.additional.placeholder',
                            sourceName: 'Settings',
                        })}
                        handleChange={SettingsActions.handleAdditionalProtocolChange}
                        handleRemove={SettingsActions.removeAdditionalProtocol}
                        index={index}
                    />
                ))}
                <div className="w-fit">
                    <Button
                        variant="secondary"
                        onClick={() =>
                            SettingsActions.updateCurrentSettings({
                                additionalAllowedProtocols: [...(Currentsettings.additionalAllowedProtocols || []), ''],
                            })
                        }
                    >
                        {i18n.translate({ id: 'allowedProtocols.additional.add', sourceName: 'Settings' })}
                    </Button>
                </div>
            </div>

            <div className="mt-5 text-right">
                <Button onClick={SettingsActions.handleUpdateSettings}>
                    {i18n.translate({ id: 'save', sourceName: 'Main' })}
                </Button>
            </div>
        </div>
    );
}

export default DataProtection;
